import { gsap } from 'gsap';
import { easeFunction } from './ease';
import { customPropertyIds, isBuiltinProperty, registryProperty, resolveProperty } from './registry';

const sortedKeys = (track) => [...track.keys].sort((a, b) => a.time - b.time);

const easeAt = (name) => easeFunction(name);

export const sampleTrack = (track, time, property) => {
    const keys = sortedKeys(track);
    if (keys.length === 0) return undefined;
    if (property?.type === 'boolean') {
        if (time <= keys[0].time) return keys[0].value;
        let held = keys[0].value;
        keys.forEach((key) => {
            if (key.time <= time) held = key.value;
        });
        return held;
    }
    if (time <= keys[0].time) return keys[0].value;

    const last = keys[keys.length - 1];
    if (time >= last.time) return last.value;

    for (let index = 0; index < keys.length - 1; index += 1) {
        const from = keys[index];
        const to = keys[index + 1];
        if (time > to.time) continue;

        const span = to.time - from.time;
        const progress = span <= 0 ? 1 : (time - from.time) / span;
        return from.value + (to.value - from.value) * easeAt(from.ease)(progress);
    }

    return last.value;
};

export const sampleClip = (clip, time) => {
    const poseByActor = {};
    (clip.actors || []).forEach((actor) => {
        if (!actor.initial) return;
        poseByActor[actor.id] = { ...actor.initial };
    });
    clip.tracks.forEach((track) => {
        const first = sortedKeys(track)[0];
        const pose = poseByActor[track.actorId];
        if (first && time < first.time && pose && track.property in pose) return;

        const actor = (clip.actors || []).find((item) => item.id === track.actorId);
        const property = resolveProperty(track.actorId, track.property, actor?.properties);
        const value = sampleTrack(track, time, property);
        const usable = property?.type === 'boolean' ? typeof value === 'boolean' : Number.isFinite(value);
        if (!usable) return;
        const next = pose || {};
        const initial = actor?.initial?.[track.property];
        const initialIsBase = (property?.mode || 'delta') === 'delta' && Number.isFinite(initial);
        // A first key equal to the initial is the whole offset. Any other key is added on top of it.
        next[track.property] = initialIsBase && first?.value !== initial ? initial + value : value;
        poseByActor[track.actorId] = next;
    });
    return poseByActor;
};

export const captureRest = (target, propertyIds = [], binding = null, actorId = null) => {
    const custom = {};
    propertyIds.forEach((id) => {
        const spec = registryProperty(actorId, id);
        const value = typeof spec?.read === 'function' ? spec.read(target, binding) : target?.[id];
        if (typeof value === 'number' && Number.isFinite(value)) custom[id] = value;
    });

    return {
        x: target.x,
        y: target.y,
        scaleX: target.scale.x,
        scaleY: target.scale.y,
        rotation: target.rotation,
        alpha: target.alpha,
        visible: target.visible,
        rotationX: Number.isFinite(target.rotationX) ? target.rotationX : 0,
        rotationY: Number.isFinite(target.rotationY) ? target.rotationY : 0,
        custom,
    };
};

const addToCurrent = (current, sample, previous) => current - previous + sample;

const applyCustomPose = (binding, pose, rest, actorId, applied) => {
    const target = binding?.target;
    if (!target) return;

    Object.entries(pose).forEach(([id, value]) => {
        if (isBuiltinProperty(id) || !Number.isFinite(value)) return;
        const spec = registryProperty(actorId, id);
        if (typeof spec?.write === 'function') {
            spec.write(target, value, rest, binding);
            return;
        }
        const current = target[id];
        if (current != null && typeof current !== 'number') return;
        const previous = applied[id] || 0;
        target[id] = addToCurrent(typeof current === 'number' ? current : (rest.custom?.[id] ?? 0), value, previous);
        applied[id] = value;
    });
};

export const applyActorPose = (binding, pose, rest, actorId, applied = {}) => {
    const target = binding?.target;
    if (!target || !rest) return;

    if ('x' in pose) {
        target.x = addToCurrent(target.x, pose.x, applied.x || 0);
        applied.x = pose.x;
    }
    if ('y' in pose) {
        target.y = addToCurrent(target.y, pose.y, applied.y || 0);
        applied.y = pose.y;
    }

    if ('scale' in pose || 'scaleX' in pose || 'scaleY' in pose) {
        const prevUniform = applied.scale || 1;
        const prevX = applied.scaleX || 1;
        const prevY = applied.scaleY || 1;
        const uniform = pose.scale ?? prevUniform;
        const scaleX = pose.scaleX ?? prevX;
        const scaleY = pose.scaleY ?? prevY;
        const denomX = prevUniform * prevX;
        const denomY = prevUniform * prevY;
        const baseX = denomX === 0 ? 0 : target.scale.x / denomX;
        const baseY = denomY === 0 ? 0 : target.scale.y / denomY;
        target.scale.set(baseX * uniform * scaleX, baseY * uniform * scaleY);
        if ('scale' in pose) applied.scale = pose.scale;
        if ('scaleX' in pose) applied.scaleX = pose.scaleX;
        if ('scaleY' in pose) applied.scaleY = pose.scaleY;
    }

    if ('alpha' in pose) target.alpha = pose.alpha;
    if ('visible' in pose) target.visible = pose.visible === true;

    if ('rotation' in pose) {
        const currentDeg = (target.rotation * 180) / Math.PI;
        const nextDeg = addToCurrent(currentDeg, pose.rotation, applied.rotation || 0);
        applied.rotation = pose.rotation;
        target.rotation = (nextDeg * Math.PI) / 180;
    }

    if ('rotationX' in pose || 'rotationY' in pose) {
        const rotate = binding.rotate || target.rotate;
        if (typeof rotate === 'function') {
            const currentX = Number.isFinite(target.rotationX) ? target.rotationX : 0;
            const currentY = Number.isFinite(target.rotationY) ? target.rotationY : 0;
            const nextX = 'rotationX' in pose
                ? addToCurrent(currentX, pose.rotationX, applied.rotationX || 0)
                : currentX;
            const nextY = 'rotationY' in pose
                ? addToCurrent(currentY, pose.rotationY, applied.rotationY || 0)
                : currentY;
            if ('rotationX' in pose) applied.rotationX = pose.rotationX;
            if ('rotationY' in pose) applied.rotationY = pose.rotationY;
            const owner = binding.rotate ? binding : target;
            rotate.call(owner, nextX, nextY, binding.perspective ?? 900);
        }
    }

    applyCustomPose(binding, pose, rest, actorId, applied);
};

const restoreBinding = (binding, rest) => {
    const target = binding?.target;
    if (!target || !rest) return;
    target.position.set(rest.x, rest.y);
    target.scale.set(rest.scaleX, rest.scaleY);
    target.rotation = rest.rotation;
    target.alpha = rest.alpha;
    target.visible = rest.visible;
    const rotate = binding.rotate || target.rotate;
    if (typeof rotate === 'function') {
        const owner = binding.rotate ? binding : target;
        rotate.call(owner, rest.rotationX || 0, rest.rotationY || 0, binding.perspective ?? 900);
    }
    Object.entries(rest.custom || {}).forEach(([id, value]) => {
        if (target[id] == null || typeof target[id] === 'number') target[id] = value;
    });
};

export const applyClipPose = (clip, time, bindings, rests, appliedByActor = {}) => {
    const poseByActor = sampleClip(clip, time);
    Object.entries(bindings).forEach(([actorId, binding]) => {
        if (!appliedByActor[actorId]) appliedByActor[actorId] = {};
        applyActorPose(binding, poseByActor[actorId] || {}, rests[actorId], actorId, appliedByActor[actorId]);
    });
};

// Plays the clip on a GSAP timeline so callers can timeScale or kill it with the rest of a scene.
export const playClip = (clip, bindings, options = {}) => {
    const rests = {};
    Object.entries(bindings).forEach(([actorId, binding]) => {
        if (binding?.target) {
            rests[actorId] = captureRest(binding.target, customPropertyIds(clip, actorId), binding, actorId);
        }
    });

    const appliedByActor = {};
    (clip?.actors || []).forEach((actor) => {
        if (!actor.initial || !bindings[actor.id] || !rests[actor.id]) return;
        if (!appliedByActor[actor.id]) appliedByActor[actor.id] = {};
        applyActorPose(bindings[actor.id], actor.initial, rests[actor.id], actor.id, appliedByActor[actor.id]);
    });

    const keepFinal = (options.keepFinal ?? clip?.keepFinal) === true;
    const timeline = options.createTimeline ? options.createTimeline() : gsap.timeline();
    const length = Math.max(0, Number(clip?.length) || 0);

    const render = () => {
        const elapsed = Math.max(0, timeline.time());
        const time = length > 0 && elapsed >= length ? length : elapsed;
        applyClipPose(clip, time, bindings, rests, appliedByActor);
        options.onUpdate?.(time);
    };

    if (length > 0) {
        timeline.to({}, { duration: length, ease: 'none' }, 0);
    }
    if (clip?.loop) timeline.repeat(-1);

    const finished = timeline.eventCallback('onComplete');
    timeline.eventCallback('onComplete', () => {
        if (!keepFinal) {
            Object.entries(bindings).forEach(([actorId, binding]) => {
                restoreBinding(binding, rests[actorId]);
            });
        }
        if (typeof finished === 'function') finished();
    });
    timeline.eventCallback('onUpdate', render);
    if (options.renderImmediately !== false) render();
    return timeline;
};

export const heldValue = (track, time, property) => {
    const value = sampleTrack(track, time, property);
    if (typeof value === 'boolean') return value;
    if (Number.isFinite(value)) return Math.round(value * 1000) / 1000;
    return property?.defaultValue
        ?? resolveProperty(track.actorId, track.property)?.defaultValue
        ?? 0;
};
